/**
 * GoalKnowledgeStore — the data-access wrapper for a goal's "Documents &
 * Knowledge Base" capability.
 *
 * Strict layering: this is the ONLY goal-knowledge module that talks to the API,
 * and it does so exclusively through the {@link EngineeringVectorApi} facade's
 * `knowledgeBase` client (UploadDocument / StartIngestion / RetrieveAndGenerate).
 * It never imports ApiClient or calls fetch. The React hook
 * ({@link import('./useGoalKnowledge').useGoalKnowledge}) calls this store; pages
 * call the hook.
 *
 * All three operations target the SAME team knowledge base, which is resolved
 * server-side from service configuration — the goal's docs are uploaded under a
 * goal-scoped relative S3 prefix, then ingested into the planner KB, so every
 * KB surface (Ask Vector, task KB Research, Estimate with Vector) can use them
 * afterward.
 */

import type { EngineeringVectorApi } from '../../api';
import { KnowledgeBaseApi, type KbCitation } from '../../api';
import type {
  KnowledgeBaseProvisioningResult,
  ExecutionDescription,
} from '../../api';
import { goalKbObjectKey, GOAL_SECTION_KEY } from './goalKnowledge';

/** Result of uploading one document to a goal's KB prefix. */
export interface GoalUploadResult {
  fileName: string;
  objectKey: string;
}

/** Result of a build/update (StartIngestion) run against the planner KB. */
export interface GoalIngestResult {
  ingestionJobId: string;
  ingestionStatus: string;
}

/** Normalized result of a goal-grounded KB answer (RetrieveAndGenerate). */
export interface GoalKbAnswer {
  answer: string;
  citations: KbCitation[];
  sessionId?: string;
}

export class GoalKnowledgeStore {
  constructor(private readonly api: EngineeringVectorApi) {}

  /**
   * Uploads one text document to the goal's scoped RELATIVE prefix in the KB
   * data source's S3 bucket (the backend resolves the bucket and prefixes +
   * validates the object key). Does NOT ingest — call {@link buildKnowledgeBase}
   * once the desired documents are uploaded. Throws on failure (e.g. bucket
   * access denied).
   */
  async uploadDoc(
    goalId: string,
    fileName: string,
    content: string,
    contentType = 'text/plain',
  ): Promise<GoalUploadResult> {
    const objectKey = goalKbObjectKey(goalId, fileName);
    const uploaded = await this.api.knowledgeBase.uploadDocument({
      objectKey,
      content,
      contentType,
      // Phase 4 separation: route the goal doc upload to the separate Goal KB.
      bundleKey: GOAL_SECTION_KEY,
    });
    return { fileName, objectKey: uploaded.objectKey ?? objectKey };
  }

  /**
   * Starts an ingestion (re-index) job on the planner KB's data source so newly
   * uploaded goal documents become retrievable across every KB surface. The KB
   * and data source are resolved server-side. Throws on failure (e.g. KB /
   * data-source access not granted).
   */
  async buildKnowledgeBase(): Promise<GoalIngestResult> {
    const ingestion = await this.api.knowledgeBase.startIngestion({
      // Phase 4 separation: ingest into the separate Goal KB's data source.
      bundleKey: GOAL_SECTION_KEY,
    });
    return {
      ingestionJobId: ingestion.ingestionJobId ?? '',
      ingestionStatus: ingestion.ingestionStatus ?? '',
    };
  }

  /**
   * Answers a question grounded in the team KB (RetrieveAndGenerate), returning
   * the answer, parsed citations, and the session id for conversational
   * continuity. The knowledge base and model are resolved server-side. Throws
   * on failure (e.g. KB access or generation model not enabled) — the caller
   * surfaces it inline.
   */
  async ask(query: string, sessionId?: string): Promise<GoalKbAnswer> {
    const response = await this.api.knowledgeBase.retrieveAndGenerate({
      query,
      ...(sessionId ? { sessionId } : {}),
      // Phase 4 separation: ground the answer in the separate Goal KB.
      bundleKey: GOAL_SECTION_KEY,
    });
    return {
      answer: response.answer ?? '',
      citations: KnowledgeBaseApi.parseCitations(response.citations),
      ...(response.sessionId ? { sessionId: response.sessionId } : {}),
    };
  }

  /**
   * Idempotently starts provisioning of the separate Goal knowledge base
   * (sectionKey "goal"). When the KB already exists the result carries
   * `alreadyProvisioned = true` and no executionArn (ready immediately);
   * otherwise it carries the Step Functions executionArn to poll with
   * {@link describeProvisioning}. Throws on failure.
   */
  async startProvisioning(): Promise<KnowledgeBaseProvisioningResult> {
    return this.api.knowledgeBase.startKnowledgeBaseProvisioning({
      sectionKey: GOAL_SECTION_KEY,
    });
  }

  /**
   * Polls a provisioning Step Functions execution, returning its status +
   * output (the output surfaces the per-step provisioning status). Throws on
   * failure.
   */
  async describeProvisioning(
    executionArn: string,
  ): Promise<ExecutionDescription> {
    return this.api.orchestrator.describeExecution({ executionArn });
  }
}
